import { unwrap, type RoleOut, type TeamOut } from "@sagot/api-client";
import { PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, RadioGroup, Select, Textarea, toast } from "@sagot/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { FormAlert } from "../auth/components/form-alert";
import { yearContext } from "../features/workspace/shared";
import { useErrorMessage } from "../lib/api-errors";
import { queryKeys } from "../lib/query-keys";
import { zodResolver } from "../lib/zod-resolver";
import { ALL_SCOPE, ScopePicker, isScopeComplete, type ScopeValue } from "../roles/scope-picker";
import { useApi } from "../session/session-provider";
import { useWorkspaceId } from "../session/workspace-context";
import { useSeatPrice } from "./use-seat-price";
import { MAX_INVITE_EMAILS, invitableRoles, outsideDomains, parseEmails, type SeatState } from "./team-model";

const MESSAGE_MAX = 2000;
const NO_TEAM = "";

interface InviteValues {
  emails: string;
  role_id: string;
  team_id: string;
  message: string;
  scope: ScopeValue;
}

function useInviteSchema(allowedDomains: readonly string[]) {
  const { t } = useTranslation();
  return z
    .object({
      emails: z.string(),
      role_id: z.string().min(1),
      team_id: z.string(),
      message: z.string().max(MESSAGE_MAX),
      scope: z.object({ scope_type: z.enum(["all", "agents", "teams"]), scope_ids: z.array(z.string()) }),
    })
    .superRefine((values, ctx) => {
      const parsed = parseEmails(values.emails);
      const issue = (message: string) => { ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["emails"], message }); };
      if (parsed.valid.length === 0 && parsed.invalid.length === 0) issue(t("team.invite.emailsRequired"));
      else if (parsed.invalid.length > 0) issue(t("team.invite.emailsInvalid", { emails: parsed.invalid.join(", ") }));
      else if (parsed.valid.length > MAX_INVITE_EMAILS) issue(t("team.invite.emailsTooMany", { max: MAX_INVITE_EMAILS }));
      else {
        const outside = outsideDomains(parsed.valid, allowedDomains);
        if (outside.length > 0) issue(t("team.invite.emailsInvalid", { emails: outside.join(", ") }));
      }
    });
}

function roleDescription(role: RoleOut, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (!role.is_builtin) return role.description ?? t("team.invite.roleDescriptions.custom", { count: role.permissions.length });
  return t(`team.invite.roleDescriptions.${role.key}`, { defaultValue: role.description ?? "" });
}

interface InviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: readonly RoleOut[];
  teams: readonly TeamOut[];
  seats: SeatState | null;
  allowedDomains: readonly string[];
}

/** `invite-modal`: bulk emails, role (radio cards) + scope, team, optional message → `POST /v1/invitations`. */
export function InviteDialog({ open, onOpenChange, roles, teams, seats, allowedDomains }: InviteDialogProps) {
  const { t } = useTranslation();
  const seatPrice = useSeatPrice();
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  const message = useErrorMessage();
  const choices = invitableRoles(roles);
  const defaultRole = choices.find((r) => r.key === "editor") ?? choices[0];
  const form = useForm<InviteValues>({
    resolver: zodResolver(useInviteSchema(allowedDomains)),
    defaultValues: { emails: "", role_id: defaultRole?.id ?? "", team_id: NO_TEAM, message: "", scope: ALL_SCOPE },
  });
  const scope = useWatch({ control: form.control, name: "scope" });
  const emails = parseEmails(useWatch({ control: form.control, name: "emails" }));
  const invite = useMutation({
    mutationFn: (values: InviteValues) =>
      unwrap(
        api.POST("/v1/invitations", {
          body: {
            emails: parseEmails(values.emails).valid,
            role_id: values.role_id,
            scope_type: values.scope.scope_type,
            scope_ids: values.scope.scope_ids,
            team_id: values.team_id || null,
            message: values.message.trim() || null,
          },
        }),
      ),
    onSuccess: async (result) => {
      if (result.created.length > 0) toast.success(t("team.invite.sent", { count: result.created.length, product: PRODUCT_NAME }));
      if (result.skipped.length > 0) toast.info(t("team.invite.skipped", { emails: result.skipped.join(", ") }));
      await client.invalidateQueries({ queryKey: queryKeys.invitations(wsId) });
      form.reset();
      onOpenChange(false);
    },
  });
  const hint = allowedDomains.length > 0 ? t("team.invite.emailsHintDomains", { domains: allowedDomains.map((d) => `@${d.replace(/^@/, "")}`).join(", ") }) : t("team.invite.emailsHint");
  const close = (next: boolean) => {
    if (!next) invite.reset();
    onOpenChange(next);
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        title={t("team.invite.title")}
        closeLabel={t("common.close")}
        className="max-w-xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => { close(false); }}>
              {t("team.cancel")}
            </Button>
            <Button type="submit" form="invite-form" loading={invite.isPending} disabled={!isScopeComplete(scope)}>
              <Icon name="send" />
              {t("team.invite.send")}
            </Button>
          </>
        }
      >
        <form id="invite-form" noValidate className="max-h-[60vh] space-y-4 overflow-y-auto" onSubmit={(event) => void form.handleSubmit((values) => { invite.mutate(values); })(event)}>
          {invite.isError ? <FormAlert title={message(invite.error)} /> : null}
          {seats?.full ? (
            <FormAlert tone="warning" title={t("team.invite.seatWarningLead", { limit: seats.limit ?? 0 })}>
              {seatPrice ? <strong>{t("team.seats.pricePerSeat", { price: seatPrice.format(seatPrice.unit), ...yearContext(seatPrice.per) })}</strong> : null}
              {seatPrice ? ", " : null}
              {t("team.invite.seatWarningTail")}
            </FormAlert>
          ) : null}
          <Field label={t("team.invite.emails")} hint={emails.valid.length > 0 ? `${hint} ${t("team.invite.emailsCount", { count: emails.valid.length })}` : hint} error={form.formState.errors.emails?.message}>
            <Textarea rows={3} placeholder={t("team.invite.emailsPlaceholder")} autoComplete="off" {...form.register("emails")} />
          </Field>
          <fieldset>
            <legend className="label">{t("team.invite.role")}</legend>
            <Controller
              control={form.control}
              name="role_id"
              render={({ field }) => (
                <RadioGroup aria-label={t("team.invite.role")} variant="cards" value={field.value} onValueChange={field.onChange} options={choices.map((role) => ({ value: role.id, label: role.name, description: roleDescription(role, t) }))} />
              )}
            />
          </fieldset>
          <Controller control={form.control} name="scope" render={({ field }) => <ScopePicker value={field.value} onChange={field.onChange} teams={teams} />} />
          <Field label={t("team.invite.team")}>
            <Select {...form.register("team_id")}>
              <option value={NO_TEAM}>{t("team.invite.noTeam")}</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={`${t("team.invite.message")} ${t("common.optional")}`} error={form.formState.errors.message?.message}>
            <Textarea rows={2} maxLength={MESSAGE_MAX} placeholder={t("team.invite.messagePlaceholder")} {...form.register("message")} />
          </Field>
        </form>
      </DialogContent>
    </Dialog>
  );
}
